"use client";

import { cn } from "@/lib/utils";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

export const BANNER_STORAGE_KEY = "bannerClosed";

interface BannerProps {
	title: string;
	description: string;
	link: string;
	linkText: string;
}

export default function Banner({
	title,
	description,
	link,
	linkText,
}: BannerProps) {
	const [show, setShow] = useState(false);

	useEffect(() => {
		// Check localStorage for banner state on component mount
		const bannerClosed = localStorage.getItem(BANNER_STORAGE_KEY);
		if (bannerClosed === "true") {
			setShow(false);
		}
	}, []);

	const handleClose = () => {
		setShow(false);
		// Store the closed state in localStorage
		localStorage.setItem(BANNER_STORAGE_KEY, "true");
	};

	if (!show) return null;

	return (
		<div
			className={cn(
				"relative isolate flex items-center gap-x-6 overflow-hidden bg-primary-34/10 px-6 py-2.5 sm:px-3.5 sm:before:flex-1",
			)}
		>
			<div
				aria-hidden="true"
				className="absolute left-[max(-7rem,calc(50%-52rem))] top-1/2 -z-10 -translate-y-1/2 transform-gpu blur-2xl"
			>
				<div
					style={{
						clipPath:
							"polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)",
					}}
					className="aspect-[577/310] w-[36.0625rem] bg-gradient-to-r from-[#ff80b5] to-[#9089fc] opacity-30"
				/>
			</div>
			<div
				aria-hidden="true"
				className="absolute left-[max(45rem,calc(50%+8rem))] top-1/2 -z-10 -translate-y-1/2 transform-gpu blur-2xl"
			>
				<div
					style={{
						clipPath:
							"polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)",
					}}
					className="aspect-[577/310] w-[36.0625rem] bg-gradient-to-r from-[#ff80b5] to-[#9089fc] opacity-30"
				/>
			</div>
			<div className="flex flex-wrap items-center gap-x-4 gap-y-2">
				<p className="text-sm/6 text-primary">
					<strong className="font-semibold">{title}</strong>
					<svg
						viewBox="0 0 2 2"
						aria-hidden="true"
						className="mx-2 inline size-0.5 fill-current"
					>
						<circle r={1} cx={1} cy={1} />
					</svg>
					{description}
				</p>
				<a
					href={link}
					className="flex-none rounded-full bg-primary-34 px-3.5 py-1 text-sm font-semibold text-white shadow-sm hover:bg-primary-64 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-64 cursor-pointer"
				>
					{linkText} <span aria-hidden="true">&rarr;</span>
				</a>
			</div>
			<div className="flex flex-1 justify-end">
				<button
					type="button"
					onClick={handleClose}
					className="-m-3 p-3 focus-visible:-outline-offset-4"
				>
					<span className="sr-only">Dismiss</span>
					<XIcon aria-hidden="true" className="size-5 text-primary" />
				</button>
			</div>
		</div>
	);
}
